import { useMemo } from 'react';
import AdminProTable from '@/components/admin-pro-table';
import { useRequest } from 'ahooks';
import { App, Drawer, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    commentAuditLogs,
    normalizeCommentAuditLogs,
    type CommentAuditLog,
} from '@/services/comments';
import { formatDramaTimestamp } from '@/pages/drama-review/utils';

import type { ProColumns } from '@ant-design/pro-components';

type Props = {
    open: boolean;
    commentId: string | null;
    onClose: () => void;
};

const getOperatorId = (record: CommentAuditLog) =>
    record.operatorId || record.operator_id || '';

const getCreatedAt = (record: CommentAuditLog) =>
    record.createdAt ?? record.created_at ?? null;

const getRowKey = (record: CommentAuditLog, index?: number) =>
    String(record.logId || record.id || `${getCreatedAt(record) ?? 'row'}-${index ?? 0}`);

const CommentAuditLogDrawer: React.FC<Props> = ({ open, commentId, onClose }) => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();

    const { data: response, loading } = useRequest(
        () =>
            commentId
                ? commentAuditLogs(commentId).then((r) => r.data)
                : Promise.resolve({
                      code: 200,
                      data: [] as CommentAuditLog[],
                      msg: '',
                  }),
        {
            ready: open && !!commentId,
            refreshDeps: [open, commentId],
            onError: (e) => {
                message.error(
                    (e as any)?.response?.data?.msg ??
                        formatMessage({ id: 'comments.historyFetchFailed' }),
                );
            },
        },
    );

    const rows = useMemo(() => {
        if (response?.code !== 200) return [];
        return normalizeCommentAuditLogs(response.data);
    }, [response]);

    const columns = useMemo<ProColumns<CommentAuditLog>[]>(
        () => [
            {
                title: <FormattedMessage id="comments.columns.no" />,
                width: 56,
                valueType: 'index',
            },
            {
                title: <FormattedMessage id="comments.historyAction" />,
                width: 140,
                render: (_, record) => record.action || '-',
            },
            {
                title: <FormattedMessage id="comments.historyReason" />,
                ellipsis: true,
                render: (_, record) => record.reason?.trim() || '-',
            },
            {
                title: <FormattedMessage id="comments.historyOperator" />,
                width: 240,
                render: (_, record) => {
                    const operatorId = getOperatorId(record);
                    if (!operatorId) return '-';
                    return (
                        <Typography.Text copyable={{ text: operatorId }} style={{ fontSize: 12 }}>
                            {operatorId}
                        </Typography.Text>
                    );
                },
            },
            {
                title: <FormattedMessage id="comments.historyTime" />,
                width: 180,
                render: (_, record) => formatDramaTimestamp(getCreatedAt(record)),
            },
        ],
        [],
    );

    return (
        <Drawer
            destroyOnClose
            open={open}
            onClose={onClose}
            title={<FormattedMessage id="comments.historyTitle" />}
            width={900}
        >
            <AdminProTable<CommentAuditLog>
                bordered
                columns={columns}
                dataSource={rows}
                loading={loading}
                rowKey={getRowKey}
                search={false}
                pagination={false}
                options={false}
                toolBarRender={false}
                size="small"
                scroll={{ x: 700 }}
                locale={{
                    emptyText: formatMessage({ id: 'comments.historyEmpty' }),
                }}
            />
        </Drawer>
    );
};

export default CommentAuditLogDrawer;
